/* The book's history: every buy, sale, dividend, deposit, currency change,
 * share transfer and contract, newest first, the way a broker's account
 * history reads.
 *
 * It reads the records and does not keep any of its own. A trade can be
 * corrected from here with the same form the trade log uses; anything else
 * opens where it was recorded, so there is still one place to correct each
 * kind of thing. */

import { useMemo, useState } from 'react';
import { HideToggle } from '../components/HideToggle';
import { TradeForm } from '../components/TradeLog';
import { accountName } from '../lib/accounts';
import { ActivityRow } from '../components/ActivityRow';
import { plural } from '../lib/format';
import {
  activityFeed, dayLabel, filterActivity, groupByDay, type ActivityFilter, type ActivityItem,
} from '../state/activity';
import { useStore } from '../state/store';
import type { Nav } from '../App';

const FILTERS: Array<{ value: ActivityFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'trades', label: 'Trades' },
  { value: 'dividends', label: 'Dividends' },
  { value: 'cash', label: 'Cash' },
  { value: 'contracts', label: 'Contracts' },
];

/* A broker import can bring years of fills. Rendering them all at once is a
   long pause on a phone for rows nobody has scrolled to yet. */
const PAGE = 150;

export function ActivityScreen({ nav, initialQuery, initialFilter }: {
  nav: Nav;
  /** Where to open, when another screen sends you here for one venue's money. */
  initialQuery?: string;
  initialFilter?: ActivityFilter;
}) {
  const { state } = useStore();
  const { trades, dividends, cashflows, conversions, transfers, perps, holdings, accounts } = state.data;

  // History outlives a release, so a filter name from an old entry is checked.
  const [filter, setFilter] = useState<ActivityFilter>(
    () => (FILTERS.some((f) => f.value === initialFilter) ? initialFilter! : 'all'),
  );
  const [query, setQuery] = useState(initialQuery ?? '');
  const [limit, setLimit] = useState(PAGE);
  const [editing, setEditing] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const say = (m: string) => { setToast(m); window.setTimeout(() => setToast(null), 3600); };

  /* Each collection named in the dependencies rather than `state.data` as a
     whole: a list that depends on the container and not on what is in it is
     the stale-render shape this code base has already hit three times. */
  const feed = useMemo(
    () => activityFeed({ trades, dividends, cashflows, conversions, transfers, perps, holdings }),
    [trades, dividends, cashflows, conversions, transfers, perps, holdings],
  );
  const shown = useMemo(
    () => filterActivity(feed, filter, query, (id) => accountName(accounts, id)),
    [feed, filter, query, accounts],
  );
  const days = useMemo(() => groupByDay(shown.slice(0, limit)), [shown, limit]);

  const editingTrade = editing ? trades.find((t) => t.id === editing) : undefined;
  const nameOf = (id: string) => accountName(accounts, id);

  const open = (item: ActivityItem) => {
    switch (item.source.type) {
      case 'trade':
        setEditing((current) => (current === item.source.id ? null : item.source.id));
        return;
      case 'dividend':
        nav.go({ name: 'dividends' });
        return;
      case 'perp':
        nav.go({ name: 'contract', id: item.source.id });
        return;
      default:
        nav.go({ name: 'settings', group: 'money' });
    }
  };

  return (
    <div className="screen">
      <div className="screen-head">
        <button className="back" onClick={nav.back} aria-label="Back">‹</button>
        <h1>History</h1>
        <span className="meta" style={{ marginLeft: 'auto' }}>
          {plural(feed.length, 'entry', 'entries')}
        </span>
        <HideToggle />
      </div>

      <div className="seg">
        {FILTERS.map((f) => (
          <button key={f.value} aria-pressed={filter === f.value}
                  onClick={() => { setFilter(f.value); setLimit(PAGE); }}>
            {f.label}
          </button>
        ))}
      </div>

      <input className="input" type="search" aria-label="Search the history"
             placeholder="Ticker, account or note" spellCheck={false}
             style={{ marginTop: 10 }}
             value={query} onChange={(e) => { setQuery(e.target.value); setLimit(PAGE); }} />

      {shown.length === 0 ? (
        <div className="card card-pad" style={{ marginTop: 14 }}>
          <p style={{ fontSize: 13.5, lineHeight: 1.55 }}>
            {feed.length === 0 ? 'Nothing recorded yet.' : 'Nothing matches.'}
          </p>
          {feed.length === 0 ? (
            <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.5, marginTop: 8 }}>
              Buying through <strong>Add a position</strong>, selling through <strong>Reduce</strong>,
              importing a broker&rsquo;s file and recording a dividend or a deposit all land here.
            </p>
          ) : null}
        </div>
      ) : (
        days.map((group) => (
          <section key={group.day} style={{ marginTop: 0 }}>
            <div className="activity-day">{dayLabel(group.day)}</div>
            <div className="card activity-list">
              {group.items.map((item) => (
                <ActivityRow key={item.id} item={item} nameOf={nameOf}
                             open={item.source.type === 'trade' && item.source.id === editing}
                             onOpen={() => open(item)} />
              ))}
            </div>
            {editingTrade && group.items.some((i) => i.source.type === 'trade' && i.source.id === editingTrade.id) ? (
              <div style={{ marginTop: 8 }}>
                <TradeForm key={editingTrade.id} trade={editingTrade} say={say}
                           onDone={() => setEditing(null)} />
              </div>
            ) : null}
          </section>
        ))
      )}

      {shown.length > limit ? (
        <button className="btn btn-block" style={{ marginTop: 14 }}
                onClick={() => setLimit((l) => l + PAGE)}>
          Show {Math.min(PAGE, shown.length - limit)} more
        </button>
      ) : null}

      <div className="dim" style={{ fontSize: 11, marginTop: 16, lineHeight: 1.5 }}>
        Every amount is in the currency it was recorded in, not converted at today&rsquo;s rate.
        Tap a trade to correct it here; anything else opens where it was recorded. A sale from
        before this book existed shows up once it is recorded by hand under Taxes.
      </div>

      {toast ? <div className="toast">{toast}</div> : null}
    </div>
  );
}